import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from "react-table";
import { Button, Table } from "reactstrap";
import { DefaultColumnFilter } from "./filters";

// Define a default UI for filtering
const defaultColumn = { Filter: DefaultColumnFilter };

const TableContainer = ({
  columns,
  data,
  customPageSize,
  className,
  getLengthPage, totalPage, noPagination, length,
  sort, setSorted, sort_by, setSortBy, page: currentPage, emptyMessage, manualPagination = false,
  onRowClick
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      // Server-paginated pages own their filtering, sorting and page resets.
      manualPagination,
      pageCount: totalPage,
      manualFilters: manualPagination,
      manualGlobalFilter: manualPagination,
      manualSortBy: manualPagination,
      autoResetPage: !manualPagination,
      autoResetFilters: !manualPagination,
      autoResetGlobalFilter: !manualPagination,
      autoResetSortBy: !manualPagination,
      autoResetExpanded: !manualPagination,
      autoResetHiddenColumns: !manualPagination,
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const [id, setID] = useState("")

  useEffect(() => {
    setPageSize(length);
    getLengthPage && getLengthPage(currentPage ?? pageNo, length)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length]);

  const generateSortingIndicator = column => {
    if (sort === "asc") {
      return <i className="ri-arrow-up-s-fill" style={{paddingLeft: "5px"}}></i>
    } else if (sort === "desc")
      return <i className="ri-arrow-down-s-fill" style={{paddingLeft: "5px"}}></i>
  };

  const handleSort = (columnID, idx) => {
    setSortBy && setSortBy(columnID?.columnID)
    setID(idx)
    sort === "asc" ? setSorted("desc") : setSorted("asc")
  }

  const [pageNo, setPage] = useState(currentPage || 1)

  useEffect(() => {
    if (currentPage !== undefined) setPage(currentPage);
  }, [currentPage]);

  // const generateSortingIndicator = column => {
  //   return column.isSorted ? (column.isSortedDesc ? <i className="ri-arrow-down-s-fill p-2"></i> : <i className="ri-arrow-up-s-fill p-2"></i>) : <i className="ri-expand-up-down-fill p-2"></i>;
  // };

  const handleChangePageNext = () => {
    setPage(pageNo + 1)
    getLengthPage(pageNo + 1, pageSize)

  };

  const handleChangePagePrevious = event => {
    setPage(pageNo - 1)
    getLengthPage(pageNo - 1, pageSize)

  };

  const handleChangePage = event => {
    setPage(event)
    getLengthPage(event, pageSize)

  };
  const isInteractiveTarget = target => target.closest("a, button, input, select, textarea, [role='button'], [role='menuitem']");
  const activateRow = (event, row) => {
    if (!onRowClick || isInteractiveTarget(event.target)) return;
    onRowClick(row.original);
  };
  return (
    <Fragment>
      <div>
        <div className="table-responsive react-table">
          <Table hover {...getTableProps()} className={className} style={{ backgroundColor: "white" }}>
            <thead className="table-light table-nowrap">
              {headerGroups.map((headerGroup, idx) => (
                <tr key={idx} style={{ backgroundColor: "#F4F6F8", cursor: "pointer" }}>
                  {headerGroup.headers.map((column, idx) => (
                    <th key={idx} style={{ paddingTop: "0.45rem", paddingBottom: "0.45rem", width: column.width, backgroundColor: "transparent" }} className={`${column.classStyle}`}>
                      <div className={`mt-1 ${column.classStyle} d-flex grey-text font-size-14 font-weight-semibold`} onClick={(e) => columns[idx]?.columnID ? handleSort(columns[idx], idx) : ""}>
                        {column.render("Header")}
                        {columns[idx]?.columnID ? id === idx ? generateSortingIndicator(column) : <i className="ri-expand-up-down-fill" style={{paddingLeft: "5px"}}></i> : ""}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {data.length === 0 && <tr><td colSpan={columns.length} className="text-center text-muted py-4">{emptyMessage}</td></tr>}
              {page.map((row, idx) => {
                prepareRow(row);
                return (
                  <tr
                    key={idx}
                    onClick={event => activateRow(event, row)}
                    onKeyDown={event => {
                      if ((event.key === "Enter" || event.key === " ") && !isInteractiveTarget(event.target)) {
                        event.preventDefault();
                        onRowClick?.(row.original);
                      }
                    }}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? "link" : undefined}
                    style={onRowClick ? { cursor: "pointer" } : undefined}
                  >
                    {row.cells.map((cell, idx) => (
                      <td key={idx} className="font-size-14" style={{ paddingTop: "0.45rem", paddingBottom: "0.45rem" }}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {
          !noPagination &&
          <Stack p={2} direction={"row"} justifyContent={"flex-end"}>
            <div className="d-flex gap-1">
              <Button
                color="primary-button"
                onClick={(e) => handleChangePage(1)}
                disabled={pageNo <= 1}
              >
                <i className="ri-arrow-left-double-line"></i>
              </Button>
              <Button
                color="primary-button"
                size="small"
                onClick={() => handleChangePagePrevious()}
                disabled={pageNo <= 1}
              >
                <i className="ri-arrow-left-s-line"></i>
              </Button>
            </div>
            <Typography p={1}>Page{" "}<strong>{pageNo}</strong></Typography>
            <div className="d-flex gap-1">
              <Button color="primary-button" onClick={() => handleChangePageNext()} disabled={pageNo >= totalPage}>
                <i className="ri-arrow-right-s-line"></i>
              </Button>
              <Button
                color="primary-button"
                onClick={(e) => handleChangePage(totalPage)}
                disabled={pageNo >= totalPage}
              >
                <i className="ri-arrow-right-double-line"></i>
              </Button>
            </div>
          </Stack>

        }
      </div>


    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
